<template>
  <!-- ========== MAIN CONTENT ========== -->
  <main id="content" role="main">
    <!-- Article -->
    <div class="container content-space-3">
      <div class="row justify-content-lg-center">
        <div class="col-lg-8">
          <!-- Back to Blog -->
          <a href="/blog" class="btn btn-ghost-secondary btn-sm mb-4">
            <i class="bi-arrow-left me-1"></i> Back to Blog
          </a>

          <!-- Article -->
          <article v-if="post">
            <!-- Article Header -->
            <header class="mb-5">
              <h1 class="display-5 mb-4">{{ post.title }}</h1>
              
              <!-- Meta info -->
              <div class="d-flex flex-wrap gap-3 text-muted mb-4">
                <span v-if="post.publishedDate">
                  <i class="bi-calendar me-1"></i>{{ formatDate(post.publishedDate) }}
                </span>
                <span v-if="post.author">
                  <i class="bi-person me-1"></i>{{ post.author }}
                </span>
              </div>

              <!-- Tags -->
              <div v-if="post.tags" class="mb-4">
                <a v-for="tag in post.tags.split(',')"
                   :key="tag"
                   :href="`/blog/tag/${slugifyTag(tag)}`"
                   class="badge bg-soft-primary text-primary me-2">
                  {{ tag.trim() }}
                </a>
              </div>
            </header>

            <!-- Featured Image -->
            <figure v-if="post.featuredImage" class="mb-5 featured-image-container">
              <img 
                class="img-fluid rounded-3 featured-image-article" 
                :src="post.featuredImage" 
                :alt="post.title"
              >
              <figcaption v-if="post.imageCaption" class="figure-caption text-center mt-3">
                {{ post.imageCaption }}
              </figcaption>
            </figure>

            <!-- Article Content -->
            <div class="article-content" v-html="post.content"></div>
          </article>

          <!-- Error State -->
          <div v-else class="alert alert-danger" role="alert">
            <i class="bi-exclamation-triangle me-2"></i>Article not found
          </div>

          <!-- Share buttons -->
          <div class="border-top pt-5 mt-5">
            <h3 class="h6 mb-4">Share this article:</h3>
            <div class="d-flex gap-2">
              <a 
                :href="`https://twitter.com/intent/tweet?text=${encodeURIComponent(post?.title || '')}&url=${encodeURIComponent(currentUrl)}`"
                target="_blank"
                class="btn btn-soft-secondary btn-sm"
                rel="noopener"
              >
                <i class="bi-twitter"></i> Twitter
              </a>
              <a 
                :href="`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`"
                target="_blank"
                class="btn btn-soft-secondary btn-sm"
                rel="noopener"
              >
                <i class="bi-linkedin"></i> LinkedIn
              </a>
              <a 
                :href="`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`"
                target="_blank"
                class="btn btn-soft-secondary btn-sm"
                rel="noopener"
              >
                <i class="bi-facebook"></i> Facebook
              </a>
              <button 
                @click="copyToClipboard"
                class="btn btn-soft-secondary btn-sm"
              >
                <i class="bi-link-45deg"></i> Copy Link
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- End Article -->
  </main>
  <!-- ========== END MAIN CONTENT ========== -->
</template>

<script>
import { toast } from 'vue3-toastify';

export default {
  name: 'BlogArticle',
  data() {
    return {
      post: window._articleData?.post || null,
      tenantName: window._articleData?.tenantName || 'Our'
    };
  },
  computed: {
    currentUrl() {
      return window.location.href;
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    },
    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.currentUrl);
        toast.success('Link copied to clipboard!');
      } catch (err) {
        toast.error('Failed to copy link');
      }
    },
    slugifyTag(tag) {
      return tag.trim()
        .toLowerCase()
        .replace(/[^\w\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-')     // Replace spaces with hyphens
        .replace(/-+/g, '-')      // Replace multiple hyphens with single
        .replace(/^-|-$/g, '');   // Trim hyphens from ends
    }
  },
  mounted() {
    // Add schema markup to head if available
    if (this.post?.schemaMarkup) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = this.post.schemaMarkup;
      document.head.appendChild(script);
    }
  }
};
</script>

<style scoped>
/* Featured image styling */
.featured-image-container {
  position: relative;
  overflow: hidden;
  border-radius: 0.75rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}

.featured-image-article {
  width: 100%;
  height: auto;
  transition: transform 0.5s ease;
  border: 1px solid rgba(0,0,0,0.08);
}

.featured-image-container:hover .featured-image-article {
  transform: scale(1.02);
}

/* Add a subtle gradient overlay for better text readability if caption exists */
.featured-image-container::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100px;
  background: linear-gradient(to top, rgba(0,0,0,0.05), transparent);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.featured-image-container:hover::after {
  opacity: 1;
}

.figure-caption {
  font-size: 0.875rem;
  color: #6c757d;
  font-style: italic;
}

.article-content {
  font-size: 1.125rem;
  line-height: 1.8;
}

.article-content :deep(h1),
.article-content :deep(h2),
.article-content :deep(h3),
.article-content :deep(h4),
.article-content :deep(h5),
.article-content :deep(h6) {
  font-weight: 600;
  margin-top: 2rem;
  margin-bottom: 1rem;
}

.article-content :deep(p) {
  margin-bottom: 1.25rem;
}

.article-content :deep(ul),
.article-content :deep(ol) {
  margin-bottom: 1.25rem;
  padding-left: 1.5rem;
}

.article-content :deep(li) {
  margin-bottom: 0.5rem;
}

.article-content :deep(blockquote) {
  border-left: 4px solid #dee2e6;
  padding-left: 1rem;
  margin: 1.5rem 0;
  font-style: italic;
  color: #6c757d;
}

.article-content :deep(pre) {
  background-color: #f8f9fa;
  border-radius: 0.375rem;
  padding: 1rem;
  margin-bottom: 1.25rem;
  overflow-x: auto;
}

.article-content :deep(code) {
  background-color: #f8f9fa;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-size: 0.875em;
  color: #e83e8c;
}

.article-content :deep(pre code) {
  background-color: transparent;
  padding: 0;
  color: inherit;
}

.article-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 0.375rem;
}

.article-content :deep(table) {
  width: 100%;
  margin-bottom: 1.25rem;
  border-collapse: collapse;
}

.article-content :deep(th),
.article-content :deep(td) {
  padding: 0.75rem;
  border: 1px solid #dee2e6;
}

.article-content :deep(th) {
  background-color: #f8f9fa;
  font-weight: 600;
}
</style>